import { useEffect, useState } from "react";
import { api, apiError, fileUrl, money } from "@/lib/api";
import { Text, Area, Select, Toggle, Modal, ImagesEditor } from "@/components/admin/AdminUI";
import { toast } from "sonner";
import { Plus, Pencil, Archive, ArchiveRestore, Calculator } from "lucide-react";

const empty = { name: "", category_id: "", description: "", base_price: "", fabric_required_in: "", images: [], available_for_custom: true, unavailable: false, hidden: false, allow_rhinestones: true, allow_ruffles: false, allow_hardware: false, status: "draft" };

function CuttingCalculator({ onApply }) {
  const [rows, setRows] = useState([{ w: "", h: "", qty: "" }]);
  const [width, setWidth] = useState("");
  const [waste, setWaste] = useState(15);
  const setRow = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  let total = 0;
  const wNum = Number(width) || 0;
  for (const r of rows) {
    const w = Number(r.w) || 0, h = Number(r.h) || 0, q = Number(r.qty) || 0;
    if (!w || !h || !q || !wNum) continue;
    const perRow = Math.max(1, Math.floor(wNum / w));
    const nrows = Math.ceil(q / perRow);
    total += nrows * h;
  }
  const suggested = Math.ceil(total * (1 + (Number(waste) || 0) / 100) * 2) / 2;

  return (
    <div className="rounded border border-kys-blush-border bg-kys-blush p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-kys-ink"><Calculator className="h-3.5 w-3.5" /> Cutting calculator (estimate linear inches)</p>
      <div className="mb-2 grid grid-cols-2 gap-2">
        <Text label="Fabric width (in)" type="number" value={width} onChange={setWidth} testid="calc-width" />
        <Text label="Waste %" type="number" value={waste} onChange={setWaste} testid="calc-waste" />
      </div>
      <p className="mb-1 text-[11px] font-medium text-kys-charcoal">Pieces (W × H × qty)</p>
      {rows.map((r, i) => (
        <div key={i} className="mb-2 grid grid-cols-3 gap-2">
          <Text label="" type="number" value={r.w} onChange={(v) => setRow(i, "w", v)} />
          <Text label="" type="number" value={r.h} onChange={(v) => setRow(i, "h", v)} />
          <Text label="" type="number" value={r.qty} onChange={(v) => setRow(i, "qty", v)} />
        </div>
      ))}
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => setRows((rs) => [...rs, { w: "", h: "", qty: "" }])} className="text-xs font-semibold text-kys-accent hover:underline">+ Add piece</button>
        {rows.length > 1 && <button type="button" onClick={() => setRows((rs) => rs.slice(0, -1))} className="text-xs text-kys-muted hover:underline">Remove last</button>}
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-kys-blush-border pt-2">
        <span data-testid="calc-suggested" className="text-sm font-semibold text-kys-ink">≈ {suggested}" per piece</span>
        <button type="button" data-testid="calc-apply" onClick={() => onApply(suggested)} disabled={!suggested} className="rounded-full bg-kys-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-kys-accent disabled:opacity-50">Use this</button>
      </div>
    </div>
  );
}

export default function AdminStyles() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/admin/styles").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/admin/categories").then((r) => setCats(r.data)); }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        base_price: form.base_price === "" || form.base_price === null ? null : Number(form.base_price),
        fabric_required_in: form.fabric_required_in === "" || form.fabric_required_in === null ? null : Number(form.fabric_required_in),
      };
      if (editId) await api.put(`/admin/styles/${editId}`, payload);
      else await api.post("/admin/styles", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const archive = async (s) => { await api.patch(`/admin/styles/${s.id}/archive?value=${!s.archived}`); load(); };
  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-3xl text-kys-ink">Style Library</h1><p className="text-sm text-kys-muted">Kys designs available for custom orders.</p></div>
        <button data-testid="new-style-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="flex items-center gap-1.5 rounded-full bg-kys-ink px-4 py-2 text-sm font-semibold text-white hover:bg-kys-accent"><Plus className="h-4 w-4" /> New</button>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((s) => (
          <div key={s.id} data-testid={`style-card-${s.name.toLowerCase()}`} className={`overflow-hidden rounded-lg border border-kys-blush-border bg-white ${s.archived ? "opacity-50" : ""}`}>
            <div className="aspect-[3/4] bg-kys-blush">{s.images?.[0] ? <img src={fileUrl(s.images[0])} alt={s.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-kys-muted">No photo</div>}</div>
            <div className="p-3">
              <p className="font-medium text-kys-ink">{s.name}</p>
              <p className="text-xs text-kys-muted">{catName(s.category_id)}</p>
              <p className="font-mono text-xs text-kys-charcoal">{s.base_price != null ? money(s.base_price) : "Price not set"}</p>
              {s.fabric_required_in != null && s.fabric_required_in > 0 && <p className="text-[11px] text-kys-muted">{s.fabric_required_in}" fabric / piece</p>}
              <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                <span className={`rounded px-1.5 py-0.5 ${s.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{s.status}</span>
                {s.unavailable && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-700">unavailable</span>}
                {s.hidden && <span className="rounded bg-gray-200 px-1.5 py-0.5">hidden</span>}
              </div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => { setForm({ ...empty, ...s, category_id: s.category_id || "", fabric_required_in: s.fabric_required_in ?? "" }); setEditId(s.id); setOpen(true); }} className="text-kys-muted hover:text-kys-accent"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => archive(s)} className="text-kys-muted hover:text-kys-accent">{s.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Style" : "New Style"} wide>
        <div className="space-y-4">
          <Text label="Style name" value={form.name} onChange={(v) => set("name", v)} testid="sf-name" />
          <Select label="Category" value={form.category_id} onChange={(v) => set("category_id", v)} options={[{ value: "", label: "—" }, ...cats.map((c) => ({ value: c.id, label: c.name }))]} />
          <Area label="Description" value={form.description} onChange={(v) => set("description", v)} />
          <Text label="Base Price ($)" type="number" value={form.base_price} onChange={(v) => set("base_price", v)} testid="sf-base-price" hint="Starting price for this design in custom orders. Leave blank for 'Price not set'." />

          <div className="rounded border border-kys-blush-border p-3">
            <Text label="Fabric required per piece (inches)" type="number" value={form.fabric_required_in} onChange={(v) => set("fabric_required_in", v)} testid="sf-fabric-required" hint="Linear inches of fabric this design uses. Leave blank/0 if you don't track material for it. Deducted from the chosen fabric's stock when an order is accepted." />
            <div className="mt-3"><CuttingCalculator onApply={(v) => set("fabric_required_in", v)} /></div>
          </div>

          <ImagesEditor value={form.images} onChange={(v) => set("images", v)} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Toggle label="Available for custom" checked={form.available_for_custom} onChange={(v) => set("available_for_custom", v)} testid="sf-available" />
            <Toggle label="Temporarily unavailable" checked={form.unavailable} onChange={(v) => set("unavailable", v)} />
            <Toggle label="Hidden from customers" checked={form.hidden} onChange={(v) => set("hidden", v)} />
          </div>
          <Select label="Status" value={form.status} onChange={(v) => set("status", v)} options={[{ value: "draft", label: "Draft (hidden)" }, { value: "published", label: "Published (live)" }]} />
          <button data-testid="save-style" onClick={save} className="w-full rounded-full bg-kys-ink py-3 text-sm font-semibold text-white hover:bg-kys-accent">Save Style</button>
        </div>
      </Modal>
    </div>
  );
}
